import { appGetState, appNextState, appUpdateState } from 'store'
import { inputs, INF_MODEL } from '../models/complex-model/inputs'
import { createOriginalModelRun, IRunItem } from '../utils/modelHelpers'
import { IScenario, scenarios } from '../models/complex-model/scenarios'
import { IStoredRun } from '../store/state'
import { palette } from 'config/palette'

const comment = (m: string) => `SimulationService::${m}`

class SimulationService {
  private appGetState = appGetState
  private appNextState = appNextState
  private appUpdateState = appUpdateState
  private runSequence = 1

  /**
   */
  public updateValue = (inputVar: string, value: number) => {
    this.appUpdateState(s => s.simulation.values[inputVar] = value, comment('updateValue'))
  }

  /**
   */
  public calcRun = (values: TObjectAny): IRunItem[] => {
    // const sketches = this.createSketches(values, 100)
    const _inputs = inputs.map(input => {
      return {
        // @ts-ignore
        element: input.key[1],
        value: values[input.var] || input.value,
        start: 0,
      }
    })
    try {
      const run = createOriginalModelRun({ outputs, settings: { ...BASE_SETTINGS, inputs: _inputs } })
      this.appUpdateState(s => s.simulation.run = run, comment('calcRun'))
      return run
    } catch (e) {
      window.logger.warn('Model calc fail', e)
      return this.appGetState().simulation.run
    }
  }

  /**
   */
  public storeCurrentRun = (scenario?: IScenario) => {
    this.appUpdateState(s => {
      const lastColorIndex = s.simulation.storedRuns.length
        ? palette.indexOf(s.simulation.storedRuns[s.simulation.storedRuns.length - 1].color)
        : -1
      // loop colors when all used
      const nextColorIndex = palette.length > lastColorIndex ? lastColorIndex + 1 : 0
      const name = scenario ? scenario.name : `Scenario ${this.runSequence}`
      s.simulation.storedRuns.push({
        name,
        key: Date.now(),
        isActive: name === 'Base Case',
        isDefaultScenario: !!scenario,
        color: palette[nextColorIndex],
        values: scenario ? { ...scenario.values } : { ...s.simulation.values },
        run: (scenario ? this.calcRun(scenario.values) : s.simulation.run)
          .map(i => ({
            ...i,
            name,
            id: `${Math.random()}`,
          })),
      })
    }, comment('storeCurrentRun'))
    this.runSequence += 1
  }

  public applyRunToCurrentInputs = (run: IStoredRun) => {
    for (const [key, value] of Object.entries(run.values)) {
      this.updateValue(key, value)
    }
  }

  /**
   */
  public reset = () => {
    this.appUpdateState(s => {
      s.simulation.values = inputs.reduce((o, i) => ({ ...o, [i.var]: i.value }), {})
      s.simulation.storedRuns.forEach(run => {
        run.isActive = false
      })
    }, comment('reset'))
  }

  public removeStoredRun = (index: number) => {
    this.appUpdateState(s => {
      s.simulation.storedRuns.splice(index, 1)
    }, comment('removeStoredRun'))
  }

  /**
   */
  public setRunName = (key: number | string, name: string) => {
    this.appUpdateState(s => {
      const run = s.simulation.storedRuns.find(i => i.key === key)
      if (run) {
        run.name = name
        run.run.forEach(r => r.name = name)
      }
    }, comment('setRunName'))
  }

  /**
   */
  public toggleStoredRun = (storedRun: IStoredRun) => {
    this.appUpdateState(s => {
      const run = s.simulation.storedRuns.find(i => i.key === storedRun.key)
      if (run) {
        run.isActive = !run.isActive
      }
    }, comment('toggleStoredRun'))
  }

  public initDefaultScenarios = () => {
    if (appGetState().simulation.defaultScenariosInitialised) {
      return
    }
    scenarios.map(i => this.storeCurrentRun(i))
    this.appUpdateState(s => {
      s.simulation.defaultScenariosInitialised = true
    }, comment('initDefaultScenarios'))
  }
}

const outputs = [
  [INF_MODEL, 'Susceptible Population'],
  [INF_MODEL, 'Infected Population'],
  [INF_MODEL, 'Recovered Population'],
  [INF_MODEL, 'Deaths'],
]

const BASE_SETTINGS = {
  startTime: 0,
  resolution: 1,
  stepCount: 90,
  sketches: {},
}

const simulationService = new SimulationService()
export { simulationService, outputs }
